import {
  Alert,
  AnimatedContentLoader,
  Button,
  DropdownMenu,
  LucaIcon,
  TextTooltip
} from '@components'
import { NocfoPlan } from '@constants'
import { PermissionSet, useBusinessContext, usePermission, usePermissionsLoaded } from '@context'
import { useQueryParam } from '@hooks'
import { fetchBusiness, IBusiness, useLucaUsage } from '@query'
import { useQuery } from '@tanstack/react-query'
import { formatDate, formatNumber } from '@utils'
import { isLucaPaygBlockedByMissingPaymentMethod } from '@utils/lucaPaygBilling'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  FaChartLine,
  FaChevronRight,
  FaEllipsisH,
  FaLayerGroup,
  FaLock,
  FaQuestionCircle,
  FaSyncAlt,
  FaWallet
} from 'react-icons/fa'
import styled, { DefaultTheme } from 'styled-components'
import { LucaBundlePurchaseModal } from './LucaBundlePurchaseModal'
import { LucaCreditLimitModal } from './LucaCreditLimitModal'
import { LucaPaygConsentModal } from './LucaPaygConsentModal'
import { LucaPaygMissingPaymentMethodAlert } from './LucaPaygMissingPaymentMethod'
import {
  buildConsumptionSegments,
  ConsumptionKind,
  deriveLucaUsageView,
  LucaUsageDerived
} from './lucaUsageVisuals'

export const LucaUsageSection: React.FC = () => {
  const { t } = useTranslation()
  const { slug, data } = useBusinessContext()
  const { data: snapshot, isLoading, isError, refetch, isFetching } = useLucaUsage(slug)
  const [panel, setPanel] = useQueryParam<string>('panel')
  const isAdmin = usePermission(PermissionSet.ADMIN)
  const permissionsLoaded = usePermissionsLoaded()
  const { isFetching: isBusinessFetching, isLoading: isBusinessLoading } = useQuery<IBusiness>({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug }),
    enabled: Boolean(slug)
  })
  // List-cache initialData omits luca_payg_*; wait for the detail refetch before rejecting ?panel=payg.
  const businessSettled = Boolean(slug) && !isBusinessLoading && !isBusinessFetching
  const [limitModalOpen, setLimitModalOpen] = useState(false)
  const paygAllowed = Boolean(data?.luca_payg_allowed)
  const paygOffered = Boolean(data?.luca_payg_available || data?.luca_payg_allowed)
  const bundleRequested = panel === 'bundles'
  const paygRequested = panel === 'payg'
  const panelRejected =
    permissionsLoaded &&
    ((bundleRequested && !isAdmin) ||
      (paygRequested && businessSettled && (!isAdmin || !paygOffered)))
  useEffect(() => {
    if (panelRejected) setPanel(undefined)
  }, [panelRejected, setPanel])
  const bundleModalOpen = bundleRequested && isAdmin
  const paygModalOpen = paygRequested && isAdmin && paygOffered
  const closeTopOffModal = () => setPanel(undefined)
  const paygBlockedWithoutPaymentMethod = isLucaPaygBlockedByMissingPaymentMethod(data)

  const view = snapshot ? deriveLucaUsageView(snapshot, data?.credit_cap ?? null) : null
  const showOverage = paygOffered || (view?.overageUsed ?? 0) > 0
  const planName = t(`constants.nocfoPlan.${data?.subscription_plan ?? NocfoPlan.FREE}`)
  const renewsAt = snapshot ? formatDate(dayjs(snapshot.period_end).add(1, 'day')) : ''

  return (
    <LucaUsageLayout>
      {paygBlockedWithoutPaymentMethod && <LucaPaygMissingPaymentMethodAlert />}
      {isError ? (
        <Alert
          type="error"
          title={t('settings.billing.lucaUsage.errorTitle')}
          description={
            <ErrorContent>
              <span>{t('settings.billing.lucaUsage.errorDescription')}</span>
              <Button size="sm" onClick={() => refetch()} showSpinner={isFetching}>
                {t('general.retry')}
              </Button>
            </ErrorContent>
          }
        />
      ) : (
        <AnimatedContentLoader isLoading={isLoading}>
          {!snapshot || !view ? (
            <Alert
              type="info"
              title={t('settings.billing.lucaUsage.emptyTitle')}
              description={t('settings.billing.lucaUsage.emptyDescription')}
            />
          ) : (
            <>
              <UsageTiles
                view={view}
                planName={planName}
                renewsAt={renewsAt}
                paygAllowed={paygAllowed}
                showOverage={showOverage}
              />
              {isAdmin && (
                <BuyRow type="button" onClick={() => setPanel('bundles')}>
                  <BuyIcon size="22px" />
                  <BuyCopy>
                    <BuyTitle>{t('settings.billing.lucaUsage.buyCreditsTitle')}</BuyTitle>
                    <BuySubtitle>{t('settings.billing.lucaUsage.buyCreditsSubtitle')}</BuySubtitle>
                  </BuyCopy>
                  <BuyChevron />
                </BuyRow>
              )}
              <ConsumptionBlock
                view={view}
                periodStart={formatDate(snapshot.period_start)}
                periodEnd={formatDate(snapshot.period_end)}
                resetDate={renewsAt}
                showPayg={isAdmin && paygOffered}
                showOverage={showOverage}
                onEditLimit={() => setLimitModalOpen(true)}
                onEditPayg={() => setPanel('payg')}
              />
              <LucaCreditLimitModal
                isVisible={limitModalOpen}
                onClose={() => setLimitModalOpen(false)}
                view={view}
                periodEnd={snapshot.period_end}
              />
              <LucaBundlePurchaseModal
                isVisible={bundleModalOpen}
                onClose={closeTopOffModal}
                balanceRemaining={view.balanceRemaining}
              />
            </>
          )}
        </AnimatedContentLoader>
      )}
      <LucaPaygConsentModal isVisible={paygModalOpen} onClose={closeTopOffModal} />
    </LucaUsageLayout>
  )
}

type MeterTone = 'navy' | 'blue' | 'sky'

const ratioMeter = (
  used: number,
  cap: number,
  tone: MeterTone
): { width: number; tone: MeterTone } | null =>
  cap > 0 ? { width: (used / cap) * 100, tone } : null

const UsageTiles: React.FC<{
  view: LucaUsageDerived
  planName: string
  renewsAt: string
  paygAllowed: boolean
  showOverage: boolean
}> = ({ view, planName, renewsAt, paygAllowed, showOverage }) => {
  const { t } = useTranslation()
  const hasPlan = view.planCredits > 0
  const hasAddon = view.addonCredits > 0
  const balanceSpent = view.balanceRemaining <= 0
  const dash = t('settings.billing.lucaUsage.includedEmptyValue')

  return (
    <TileGrid>
      <UsageTile
        icon={<FaSyncAlt />}
        label={t('settings.billing.lucaUsage.includedCredits')}
        tooltip={t('settings.billing.lucaUsage.includedTooltip')}
        value={hasPlan ? formatNumber(view.planUsed) : dash}
        total={hasPlan ? formatNumber(view.planCredits) : undefined}
        muted={!hasPlan}
        meter={ratioMeter(view.planUsed, view.planCredits, 'navy')}
        hint={
          hasPlan
            ? t('settings.billing.lucaUsage.includedHintPlanOnly', {
                plan: planName,
                date: renewsAt
              })
            : t('settings.billing.lucaUsage.includedHintNone')
        }
      />
      {hasAddon && (
        <UsageTile
          icon={<FaLayerGroup />}
          label={t('settings.billing.lucaUsage.addonCredits')}
          tooltip={t('settings.billing.lucaUsage.addonTooltip')}
          value={formatNumber(view.addonUsed)}
          total={formatNumber(view.addonCredits)}
          meter={ratioMeter(view.addonUsed, view.addonCredits, 'blue')}
          hint={t('settings.billing.lucaUsage.addonHint', { date: renewsAt })}
        />
      )}
      {view.balanceGranted > 0 && (
        <UsageTile
          icon={<FaWallet />}
          label={t('settings.billing.lucaUsage.balanceCredits')}
          tooltip={t('settings.billing.lucaUsage.balanceTooltip')}
          value={formatNumber(view.balanceUsed)}
          total={formatNumber(view.balanceGranted)}
          muted={balanceSpent}
          meter={ratioMeter(view.balanceUsed, view.balanceGranted, 'sky')}
          hint={
            balanceSpent
              ? t('settings.billing.lucaUsage.balanceHintSpent')
              : t('settings.billing.lucaUsage.balanceHint', {
                  remaining: formatNumber(view.balanceRemaining)
                })
          }
        />
      )}
      {showOverage && (
        <UsageTile
          icon={<FaChartLine />}
          label={t('settings.billing.lucaUsage.overage')}
          tooltip={t('settings.billing.lucaUsage.overageTooltip')}
          value={formatNumber(view.overageUsed)}
          hint={
            paygAllowed
              ? t('settings.billing.lucaUsage.paygEnabledHint')
              : view.overageUsed > 0
              ? t('settings.billing.lucaUsage.overageUsed', {
                  used: formatNumber(view.overageUsed)
                })
              : t('settings.billing.lucaUsage.overageNone')
          }
        />
      )}
    </TileGrid>
  )
}

const ConsumptionBlock: React.FC<{
  view: LucaUsageDerived
  periodStart: string
  periodEnd: string
  resetDate: string
  showPayg: boolean
  showOverage: boolean
  onEditLimit: () => void
  onEditPayg: () => void
}> = ({
  view,
  periodStart,
  periodEnd,
  resetDate,
  showPayg,
  showOverage,
  onEditLimit,
  onEditPayg
}) => {
  const { t } = useTranslation()
  const canEditLimit = usePermission(PermissionSet.ADMIN)
  const segments = buildConsumptionSegments(view)
  const legendItems: { kind: ConsumptionKind; name: string; figure: string }[] = []
  const hasLimit = view.limitPercent != null

  if (view.planCredits > 0) {
    legendItems.push({
      kind: 'plan',
      name: t('settings.billing.lucaUsage.legendPlan'),
      figure: `${formatNumber(view.planUsed)} / ${formatNumber(view.planCredits)}`
    })
  }
  if (view.addonCredits > 0) {
    legendItems.push({
      kind: 'addon',
      name: t('settings.billing.lucaUsage.legendAddon'),
      figure: `${formatNumber(view.addonUsed)} / ${formatNumber(view.addonCredits)}`
    })
  }
  if (view.balanceGranted > 0) {
    legendItems.push({
      kind: 'balance',
      name: t('settings.billing.lucaUsage.legendBalance'),
      figure: `${formatNumber(view.balanceUsed)} / ${formatNumber(view.balanceGranted)}`
    })
  }
  if (view.showOverageBar) {
    legendItems.push({
      kind: 'overage',
      name: t('settings.billing.lucaUsage.legendOverage'),
      figure: formatNumber(view.overageUsed)
    })
  }

  const limitCaptionKey = view.limitReached
    ? 'settings.billing.lucaUsage.captionLimitReached'
    : view.creditLimit != null
    ? 'settings.billing.lucaUsage.captionLimit'
    : null

  return (
    <BreakdownSection>
      <SectionHeaderRow>
        <SectionTitle>{t('settings.billing.lucaUsage.consumptionTitle')}</SectionTitle>
        {canEditLimit && (
          <DropdownMenu
            items={[
              <Button key="edit-limit" isSecondary icon={<FaLock />} onClick={onEditLimit}>
                {t('settings.billing.lucaUsage.editLimit')}
              </Button>,
              ...(showPayg
                ? [
                    <Button key="payg" isSecondary icon={<FaChartLine />} onClick={onEditPayg}>
                      {t('settings.billing.lucaUsage.paygMenu')}
                    </Button>
                  ]
                : [])
            ]}
          >
            <Button size="sm" isSecondary icon={<FaEllipsisH />} />
          </DropdownMenu>
        )}
      </SectionHeaderRow>
      <BarArea $hasLimit={hasLimit}>
        {hasLimit && view.creditLimit != null && view.limitPercent != null && (
          <>
            <LimitFlag style={{ left: `${view.limitPercent}%` }} $atEnd={view.limitPercent >= 99.5}>
              <FaLock />
              {t('settings.billing.lucaUsage.limitFlag', {
                limit: formatNumber(view.creditLimit)
              })}
            </LimitFlag>
            <LimitNotch style={{ left: `${view.limitPercent}%` }} />
          </>
        )}
        <BarTrack>
          {segments.map(segment => (
            <BarSegment
              key={segment.kind}
              $kind={segment.kind}
              $flex={segment.flex}
              style={segment.flex ? undefined : { width: `${segment.widthPercent}%` }}
            >
              {segment.kind !== 'headroom' && (
                <BarFill $kind={segment.kind} style={{ width: `${segment.fillPercent}%` }} />
              )}
            </BarSegment>
          ))}
        </BarTrack>
      </BarArea>
      <LegendList>
        {legendItems.map(item => (
          <LegendItem key={item.kind}>
            <LegendSwatch $kind={item.kind} />
            <LegendLabel>{item.name}</LegendLabel>
            <LegendValue>{item.figure}</LegendValue>
          </LegendItem>
        ))}
      </LegendList>
      <Caption>
        <Trans
          i18nKey={
            view.overageUsed > 0
              ? 'settings.billing.lucaUsage.captionWithOverage'
              : showOverage
              ? 'settings.billing.lucaUsage.caption'
              : 'settings.billing.lucaUsage.captionWithoutOverage'
          }
          values={{
            total: formatNumber(view.totalCredits),
            overage: formatNumber(view.overageUsed),
            start: periodStart,
            end: periodEnd
          }}
        />
        {limitCaptionKey && (
          <Trans
            i18nKey={limitCaptionKey}
            values={{
              limit: formatNumber(view.creditLimit ?? 0),
              date: resetDate
            }}
          />
        )}
      </Caption>
    </BreakdownSection>
  )
}

const UsageTile: React.FC<{
  icon: React.ReactNode
  label: string
  tooltip: string
  value: string
  hint?: string
  total?: string
  muted?: boolean
  meter?: { width: number; tone: MeterTone } | null
}> = ({ icon, label, tooltip, value, hint, total, muted, meter }) => {
  const { t } = useTranslation()

  return (
    <Tile>
      <TileHeader>
        <TileLabel>
          {icon}
          {label}
        </TileLabel>
        <TextTooltip tooltip={tooltip} placement="bottom-end" closeDelay={300} maxWidth={280}>
          <TileHelp aria-label={t('settings.billing.lucaUsage.tileHelpAria', { label })}>
            <FaQuestionCircle />
          </TileHelp>
        </TextTooltip>
      </TileHeader>
      <ValueRow>
        <TileValue $muted={muted}>{value}</TileValue>
        {total != null && <TileSuffix $muted={muted}>/ {total}</TileSuffix>}
      </ValueRow>
      {meter == null ? (
        <TileMeterSpacer />
      ) : (
        <TileMeter>
          <TileMeterFill $tone={meter.tone} style={{ width: `${meter.width}%` }} />
        </TileMeter>
      )}
      {hint && <TileHint>{hint}</TileHint>}
    </Tile>
  )
}

const consumptionKindBackground = (
  kind: ConsumptionKind,
  theme: DefaultTheme,
  surface: 'bar' | 'legend'
): string => {
  switch (kind) {
    case 'overage':
      return theme.colors.warning
    case 'balance':
      return surface === 'bar' ? theme.luca.sky : theme.luca.mistBorder
    case 'addon':
      return theme.luca.blue
    case 'plan':
      return theme.luca.navy
    case 'headroom':
      return theme.colors.neutralGray
    default: {
      const _exhaustive: never = kind
      return _exhaustive
    }
  }
}

const LucaUsageLayout = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  min-width: 0;
`

const TileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing.sm}rem;

  @media (max-width: ${({ theme }) => theme.breakpoint.sm}) {
    grid-template-columns: 1fr;
  }
`

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.luca.mist};
`

const TileHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const TileHelp = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1;
`

const TileLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.luca.textMuted};

  svg {
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
    color: ${({ theme }) => theme.colors.iconGray};
  }
`

const ValueRow = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 0.25rem;
  min-width: 0;
`

const TileValue = styled.span<{ $muted?: boolean }>`
  font-family: 'Roboto Mono', ui-monospace, monospace;
  font-size: ${({ theme }) => theme.fontSize.xxl}rem;
  font-weight: 600;
  color: ${({ theme, $muted }) => ($muted ? theme.colors.iconGray : theme.luca.navy)};
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
`

const TileSuffix = styled.span<{ $muted?: boolean }>`
  font-family: 'Roboto Mono', ui-monospace, monospace;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 400;
  color: ${({ theme, $muted }) => ($muted ? theme.colors.borderStrong : theme.colors.iconGray)};
  font-variant-numeric: tabular-nums;
`

const TileMeter = styled.span`
  height: 4px;
  border-radius: 100px;
  background: ${({ theme }) => theme.luca.mistBorder};
  overflow: hidden;
  display: flex;
`

const TileMeterFill = styled.span<{ $tone: MeterTone }>`
  display: block;
  height: 100%;
  background: ${({ $tone, theme }) => {
    if ($tone === 'blue') return theme.luca.blue
    if ($tone === 'sky') return theme.luca.sky
    return theme.luca.navy
  }};
`

const TileMeterSpacer = styled.span`
  height: 4px;
`

const TileHint = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const BreakdownSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
`

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const SectionTitle = styled.h4`
  margin: 0;
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.luca.navy};
`

const BarArea = styled.div<{ $hasLimit: boolean }>`
  position: relative;
  padding-top: ${({ $hasLimit, theme }) => ($hasLimit ? `${theme.spacing.lg}rem` : '0')};
`

const LimitFlag = styled.span<{ $atEnd: boolean }>`
  position: absolute;
  top: 0;
  transform: translateX(${({ $atEnd }) => ($atEnd ? '-100%' : '-50%')});
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 2px 7px;
  border-radius: 100px;
  background: ${({ theme }) => theme.luca.navy};
  color: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 600;
  white-space: nowrap;

  svg {
    font-size: 0.5rem;
  }
`

const LimitNotch = styled.span`
  position: absolute;
  top: calc(${({ theme }) => theme.spacing.lg}rem - 2px);
  width: 2px;
  height: 18px;
  transform: translateX(-1px);
  border-radius: 1px;
  background: ${({ theme }) => theme.luca.navy};
`

const BarTrack = styled.div`
  display: flex;
  gap: 3px;
  height: 14px;
`

const BarSegment = styled.div<{ $kind: ConsumptionKind; $flex: boolean }>`
  display: flex;
  min-width: ${({ $kind, $flex }) => {
    if ($flex) return '0'
    if ($kind === 'addon' || $kind === 'balance') return '40px'
    if ($kind === 'overage' || $kind === 'headroom') return '34px'
    return '0'
  }};
  flex: ${({ $flex }) => ($flex ? '1 1 0' : '0 0 auto')};
  border-radius: 4px;
  overflow: hidden;
  box-sizing: border-box;
  background: ${({ theme }) => theme.luca.mistBorder};
  ${({ $kind, theme }) => {
    if ($kind === 'overage') {
      return `
        overflow: visible;
        background: transparent;
        border: 1px dashed ${theme.colors.warning};
      `
    }
    if ($kind === 'headroom') {
      return `
        background: ${theme.colors.neutralGray};
        border: 1px dashed ${theme.colors.borderStrong};
      `
    }
    return ''
  }}
`

const BarFill = styled.div<{ $kind: ConsumptionKind }>`
  height: 100%;
  background: ${({ $kind, theme }) =>
    $kind === 'overage'
      ? `color-mix(in srgb, ${theme.colors.warning} 40%, transparent)`
      : consumptionKindBackground($kind, theme, 'bar')};
`

const LegendList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;

  @media (max-width: ${({ theme }) => theme.breakpoint.sm}) {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: stretch;
    gap: ${({ theme }) => theme.spacing.xs}rem;
  }
`

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoint.sm}) {
    width: 100%;
  }
`

const LegendSwatch = styled.span<{ $kind: ConsumptionKind }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 4px;
  flex-shrink: 0;
  box-sizing: border-box;
  background: ${({ $kind, theme }) =>
    $kind === 'overage'
      ? `color-mix(in srgb, ${theme.colors.warning} 40%, transparent)`
      : consumptionKindBackground($kind, theme, 'legend')};
  ${({ $kind, theme }) =>
    $kind === 'overage' ? `border: 1px dashed ${theme.colors.warning};` : ''}
`

const LegendLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.luca.textMuted};
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoint.sm}) {
    flex: 1;
  }
`

const LegendValue = styled.span`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.luca.navy};
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const Caption = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.iconGray};

  strong {
    color: ${({ theme }) => theme.luca.navy};
    font-weight: 600;
  }
`

const BuyRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.xl}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${({ theme }) => theme.luca.mistBorder};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const BuyIcon = styled(LucaIcon)`
  flex-shrink: 0;
  color: ${({ theme }) => theme.luca.blue};
`

const BuyCopy = styled.span`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const BuyTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  line-height: 1.5;
  color: ${({ theme }) => theme.luca.navy};
`

const BuySubtitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.luca.textMuted};
`

const BuyChevron = styled(FaChevronRight)`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
